import React, { useState, useEffect, useCallback } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { NavLink, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Form } from "react-bootstrap";
import AccountsMenu from "./AccountsMenu";

function EditOpeningBalance() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [Account, setAccount] = useState({});
  const [OpBalanceID, setOpBalanceID] = useState(0);
  const [amount, setAmount] = useState("");
  const [amountType, setAmountType] = useState("Debit");
  const [budgetAllocation, setBudgetAllocation] = useState("");
  const [openingDebit, setOpeningDebit] = useState(0);
  const [openingCredit, setOpeningCredit] = useState(0);

  const params = useParams();
  const accountId = parseInt(params.id, 10) || 0;
  const [selectedYear, setSelectedYear] = useState("");

  const applyAccountData = (account) => {
    if (!account) return;
    setAccount(account);
    const opId = Number(account.OpBalanceID ?? account.opBalanceID ?? 0);
    setOpBalanceID(opId);
    const debit = parseFloat(account.OpeningDebit ?? account.openingDebit) || 0;
    const credit = parseFloat(account.OpeningCredit ?? account.openingCredit) || 0;
    setOpeningDebit(debit);
    setOpeningCredit(credit);
    const balType =
      account.OpeningBalanceType ||
      account.openingBalanceType ||
      (credit > 0 ? "Credit" : "Debit");
    setAmountType(balType);
    const amt = account.Amount ?? account.amount;
    setAmount(amt !== undefined && amt !== null ? String(amt) : "");
    const budget = account.Budget_Allocation ?? account.budget_Allocation;
    setBudgetAllocation(
      budget !== undefined && budget !== null ? String(budget) : ""
    );
  };

  const loadAccount = useCallback(() => {
    if (accountId <= 0) {
      setLoading(false);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const fYearParam = urlParams.get("FYear");
    const fYear = fYearParam ? parseInt(fYearParam, 10) : 0;
    if (fYearParam) {
      setSelectedYear(fYearParam);
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Accounts/GetAccountByID",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        ID: accountId,
        FYear: fYear,
      },
    })
      .then((response) => {
        if (response.data?.status_code === 1) {
          applyAccountData(response.data.Account || {});
        } else {
          toast.error(
            response.data?.status_message || "Unable to load account.",
            { position: toast.POSITION.TOP_RIGHT }
          );
        }
      })
      .catch(() => {
        toast.error("Network error loading account.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, accountId]);

  useEffect(() => {
    document.title = "Add/Update Account Opening Balance";
    loadAccount();
  }, [loadAccount]);

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setValidated(true);

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      return;
    }

    const parsedAmount = parseFloat(String(amount).replace(/,/g, "")) || 0;
    const parsedBudget =
      parseFloat(String(budgetAllocation).replace(/,/g, "")) || 0;
    const fYear = parseInt(selectedYear, 10) || 0;

    if (accountId <= 0) {
      toast.error("Invalid account.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    setSaving(true);
    axios({
      method: "post",
      url: config.base_url + "Accounts/UpdateOpeningBalance",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        ID: accountId,
        FYear: fYear,
        OpBalanceID: OpBalanceID || 0,
        Fee_Amount: parsedAmount,
        Amount: Math.round(parsedAmount),
        AmountType: amountType,
        Budget_Allocation: parsedBudget,
      },
    })
      .then((response) => {
        if (response.data?.status_code === 1) {
          toast.success(response.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          loadAccount();
        } else {
          toast.error(
            response.data?.status_message || "Failed to save opening balance.",
            { position: toast.POSITION.TOP_RIGHT }
          );
        }
      })
      .catch(() => {
        toast.error("Network error saving opening balance.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setSaving(false));
  };

  if (accountId <= 0) {
    return (
      <>
        <AccountsMenu />
        <div className="container body-content p-4">
          <div className="alert alert-warning">Invalid account id.</div>
        </div>
      </>
    );
  }

  return (
    <>
      <AccountsMenu />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Accounts/AccountsDashboard">Accounts</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Edit Open Balance
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Edit / Update Opening Balance</div>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <NavLink
                    to="/Accounts/OpeningBalances"
                    className="button button-white button-icon"
                  >
                    Opening Balances
                    <i className="ri-add-line"></i>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="card">
              <div className="card-body p-md-4">
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <div className="row align-items-center">
                      <div className="col-md-3">
                        <div className="form-group mb-3">
                          <label>Selected Account Details</label>
                          <label className="label-gray d-block">
                            {(Account.Account_code || "—") +
                              " | " +
                              (selectedYear || Account.Year || "—")}
                          </label>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group mb-3">
                          <label>Account Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={Account.Account_desc || ""}
                            disabled
                          />
                        </div>
                      </div>
                      {openingDebit > 0 && openingCredit > 0 && (
                        <div className="col-md-12">
                          <div className="alert alert-warning mb-3">
                            Both Debit and Credit were found for this opening
                            balance (Debit: {openingDebit}, Credit:{" "}
                            {openingCredit}). Select the correct balance type and
                            amount, then save.
                          </div>
                        </div>
                      )}
                      <div className="col-md-3">
                        <div className="form-group mb-3">
                          <label>Opening Balance</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Enter opening balance amount.
                          </Form.Control.Feedback>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group mb-3">
                          <label>Balance Type</label>
                          <select
                            className="form-select"
                            name="ddlAmountType"
                            value={amountType}
                            onChange={(e) => setAmountType(e.target.value)}
                          >
                            <option value="Debit">Debit</option>
                            <option value="Credit">Credit</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group mb-3">
                          <label>Budget Allocation</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control"
                            name="txtBudgetAllocation"
                            value={budgetAllocation}
                            onChange={(e) =>
                              setBudgetAllocation(e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-3 offset-md-6 text-end">
                        <div className="form-group">
                          {saving ? (
                            <Spinner animation="border" role="status" />
                          ) : (
                            <button
                              type="submit"
                              className="button button-primary w-100"
                            >
                              SAVE
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditOpeningBalance;
